// Non-sensitive UI preferences only. Authentication/session data must never use this store.
import { devError } from "@/utils/logger";

const ALLOWED_PREFERENCE_KEYS = new Set([
  "iwmc_favorites",
  "iwmc_hub_bookmarks",
  "iwmc_hub_recent",
  "iwmc_market_spotlight_v3",
]);
const ALLOWED_SESSION_KEYS = new Set(["iwc_ai_session", "iwc_intake_src", "iwc_member_hint", "iwc_tts_session"]);
const ALLOWED_SESSION_PREFIXES = ["iwc-cta:"];
const LEGACY_AUTH_STORAGE_KEYS = ["access_token", "iwc_member", "refresh_token", "token"];
const MAX_PREFERENCE_VALUE_LENGTH = 100_000;
const MAX_SESSION_VALUE_LENGTH = 4_096;

function isSafeStorageValue(key, value, maxLength) {
  const allowed = typeof value === "string" && value.length <= maxLength;
  if (!allowed) devError(`Blocked oversized or non-text browser storage value: ${key}`);
  return allowed;
}

function isAllowedPreferenceKey(key) {
  const allowed = ALLOWED_PREFERENCE_KEYS.has(key);
  if (!allowed) devError(`Blocked non-preference browser storage key: ${key}`);
  return allowed;
}

function isAllowedSessionKey(key) {
  const allowed = ALLOWED_SESSION_KEYS.has(key) || ALLOWED_SESSION_PREFIXES.some((prefix) => key.startsWith(prefix));
  if (!allowed) devError(`Blocked non-transient browser storage key: ${key}`);
  return allowed;
}

export const persistentPreferences = {
  get(key) {
    if (!isAllowedPreferenceKey(key)) return null;
    try { return window.localStorage.getItem(key); } catch (error) { devError(`Unable to read preference: ${key}`, error); return null; }
  },
  set(key, value) {
    if (!isAllowedPreferenceKey(key) || !isSafeStorageValue(key, value, MAX_PREFERENCE_VALUE_LENGTH)) return;
    try { window.localStorage.setItem(key, value); } catch (error) { devError(`Unable to save preference: ${key}`, error); }
  },
  remove(key) {
    if (!isAllowedPreferenceKey(key)) return;
    try { window.localStorage.removeItem(key); } catch (error) { devError(`Unable to remove preference: ${key}`, error); }
  },
};

export const transientSession = {
  get(key) {
    if (!isAllowedSessionKey(key)) return null;
    try { return window.sessionStorage.getItem(key); } catch (error) { devError(`Unable to read transient session value: ${key}`, error); return null; }
  },
  set(key, value) {
    if (!isAllowedSessionKey(key) || !isSafeStorageValue(key, value, MAX_SESSION_VALUE_LENGTH)) return;
    try { window.sessionStorage.setItem(key, value); } catch (error) { devError(`Unable to save transient session value: ${key}`, error); }
  },
  remove(key) {
    if (!isAllowedSessionKey(key)) return;
    try { window.sessionStorage.removeItem(key); } catch (error) { devError(`Unable to remove transient session value: ${key}`, error); }
  },
};

export function clearLegacyAuthStorage() {
  for (const key of LEGACY_AUTH_STORAGE_KEYS) {
    try { window.localStorage.removeItem(key); } catch (error) { devError(`Unable to remove legacy auth storage: ${key}`, error); }
    try { window.sessionStorage.removeItem(key); } catch (error) { devError(`Unable to remove legacy auth session storage: ${key}`, error); }
  }
}

export function readJsonPreference(key, fallback) {
  try {
    const raw = persistentPreferences.get(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch (error) {
    devError(`Unable to parse preference: ${key}`, error);
    return fallback;
  }
}

export function writeJsonPreference(key, value) {
  persistentPreferences.set(key, JSON.stringify(value));
}